import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Heart, Share2, Search, Sparkles, Clock, ArrowRight } from "lucide-react";
import { Logo } from "@/shared/brand";
import {
  explore,
  likeDesign,
  getLikedSet,
  setLiked,
  type ExploreDesign,
  type ExploreCreator,
} from "@/features/showcase/exploreApi";

const CATEGORIES = ["All", "Featured", "Rings", "Earrings", "Necklaces", "Bracelets", "Pendants"];

/**
 * Public global Explore gallery — a marketplace-style discovery page for every
 * workspace's opt-in (public) designs. Featured-creators strip, category +
 * search + sort, masonry card grid with likes/share. No sign-in required.
 */
export default function ExplorePage() {
  const nav = useNavigate();
  const reduce = useReducedMotion();
  const [category, setCategory] = useState("All");
  const [sort, setSort] = useState<"liked" | "newest">("liked");
  const [q, setQ] = useState("");
  const [designs, setDesigns] = useState<ExploreDesign[] | null>(null);
  const [creators, setCreators] = useState<ExploreCreator[]>([]);
  const [liked, setLikedState] = useState<Set<string>>(() => getLikedSet());

  useEffect(() => {
    let alive = true;
    const t = setTimeout(
      () => {
        explore({ category, sort, q: q.trim() || undefined, limit: 60 })
          .then((r) => {
            if (!alive) return;
            setDesigns(r.designs);
            if (r.creators.length) setCreators(r.creators);
          })
          .catch(() => alive && setDesigns([]));
      },
      q ? 250 : 0,
    );
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [category, sort, q]);

  const toggleLike = async (d: ExploreDesign) => {
    const isLiked = liked.has(d.id);
    const next = new Set(liked);
    if (isLiked) next.delete(d.id);
    else next.add(d.id);
    setLikedState(next);
    setLiked(d.id, !isLiked);
    setDesigns((ds) =>
      ds?.map((x) => (x.id === d.id ? { ...x, likeCount: Math.max(0, x.likeCount + (isLiked ? -1 : 1)) } : x)) ?? ds,
    );
    try {
      await likeDesign(d.id, isLiked);
    } catch {
      /* keep optimistic; a refetch will reconcile */
    }
  };

  const share = async (d: ExploreDesign) => {
    if (!d.creator?.slug) return;
    const url = `${window.location.origin}/showcase/${d.creator.slug}/${d.id}`;
    try {
      if (navigator.share) await navigator.share({ title: d.name, url });
      else await navigator.clipboard.writeText(url);
    } catch {
      /* user dismissed */
    }
  };

  const open = (d: ExploreDesign) => {
    if (d.creator?.slug) nav(`/showcase/${d.creator.slug}/${d.id}`);
  };

  return (
    <div className="vault-surface min-h-screen text-ink-900">
      {/* ── Top nav ── */}
      <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-white/50 bg-white/55 px-5 py-3 backdrop-blur-xl sm:px-8">
        <Link to="/explore" className="shrink-0">
          <Logo size="sm" />
        </Link>
        <nav className="ml-4 hidden items-center gap-1 sm:flex">
          <span className="rounded-full bg-[var(--accent-soft,#efe6d4)] px-3.5 py-1.5 text-[13px] font-semibold text-[var(--accent-strong,#7c5f2e)]">
            Explore
          </span>
          <Link to="/#pricing" className="rounded-full px-3.5 py-1.5 text-[13px] font-medium text-ink-600 hover:text-ink-900">
            Pricing
          </Link>
        </nav>
        <div className="ml-auto flex items-center gap-2">
          <Link
            to="/"
            className="rounded-full border border-white/70 bg-white/70 px-4 py-2 text-[13px] font-medium text-ink-800 shadow-sm backdrop-blur transition hover:border-[var(--accent-border,#d8c7a0)]"
          >
            Sign in
          </Link>
          <Link
            to="/"
            className="btn-shine gold-gradient flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-semibold text-white shadow-[0_4px_14px_rgba(150,110,72,0.3)] transition hover:opacity-95"
          >
            Start free <ArrowRight size={14} />
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-[1500px] px-5 pb-24 pt-6 sm:px-8">
        {/* ── Featured creators strip ── */}
        {creators.length > 0 && (
          <section className="mb-7">
            <div className="mb-2.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-500">
              <Sparkles size={13} className="text-[var(--accent-strong,#a0793d)]" /> Featured studios
            </div>
            <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {creators.map((c, i) => (
                <Link
                  key={c.slug}
                  to={`/showcase/${c.slug}`}
                  title={`${c.name} · ${c.count} public`}
                  className="group relative grid h-[92px] w-[92px] shrink-0 place-items-center overflow-hidden rounded-2xl border border-white/60 shadow-sm transition hover:-translate-y-0.5 hover:shadow-pop"
                  style={{ background: CREATOR_TINTS[i % CREATOR_TINTS.length] }}
                >
                  {c.logo ? (
                    <img src={c.logo} alt="" className="h-9 w-9 rounded-xl object-cover" />
                  ) : (
                    <span className="font-headline text-[26px] text-white/95 drop-shadow">{c.name.charAt(0)}</span>
                  )}
                  <span className="absolute inset-x-1.5 bottom-1.5 truncate rounded-full bg-black/35 px-2 py-0.5 text-center text-[10.5px] font-medium text-white backdrop-blur-sm">
                    {c.name}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* ── Filters ── */}
        <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap gap-1.5">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={
                  "rounded-full border px-3.5 py-1.5 text-[13px] font-medium transition " +
                  (category === c
                    ? "border-transparent bg-[var(--accent,#a0793d)] text-white shadow-sm"
                    : "border-white/60 bg-white/60 text-ink-700 backdrop-blur hover:border-[var(--accent-border,#d8c7a0)]")
                }
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search designs…"
                className="h-9 w-48 rounded-full border border-white/60 bg-white/70 pl-8 pr-3 text-[13px] text-ink-900 outline-none backdrop-blur placeholder:text-ink-400 focus:border-[var(--accent-border,#d8c7a0)]"
              />
            </div>
            <div className="flex items-center gap-0.5 rounded-full border border-white/60 bg-white/60 p-0.5 backdrop-blur">
              {([["liked", Heart, "Most liked"], ["newest", Clock, "Newest"]] as const).map(([key, Icon, label]) => (
                <button
                  key={key}
                  onClick={() => setSort(key)}
                  className={
                    "flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12.5px] font-medium transition " +
                    (sort === key ? "bg-white text-ink-900 shadow-sm" : "text-ink-500 hover:text-ink-800")
                  }
                >
                  <Icon size={13} className={sort === key && key === "liked" ? "fill-[var(--red,#c2564e)] text-[var(--red,#c2564e)]" : ""} />
                  <span className="hidden sm:inline">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Masonry grid ── */}
        {designs === null ? (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[4/3] rounded-2xl border border-white/50 bg-white/40 backdrop-blur" />
            ))}
          </div>
        ) : designs.length === 0 ? (
          <div className="grid place-items-center rounded-2xl border border-dashed border-[var(--border-strong,#d8ccb4)] py-24 text-center text-ink-500">
            <div>
              <div className="mb-1 font-headline text-[18px] text-ink-800">No public designs yet</div>
              <p className="text-[13px]">Studios publish work here from their Vault — check back soon.</p>
            </div>
          </div>
        ) : (
          <motion.div
            className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4"
            initial="hidden"
            animate="show"
            variants={reduce ? undefined : { hidden: {}, show: { transition: { staggerChildren: 0.04 } } }}
          >
            {designs.map((d, i) => (
              <DesignCard
                key={d.id}
                d={d}
                i={i}
                liked={liked.has(d.id)}
                reduce={!!reduce}
                onOpen={() => open(d)}
                onLike={() => toggleLike(d)}
                onShare={() => share(d)}
              />
            ))}
          </motion.div>
        )}
      </main>

      <footer className="border-t border-white/50 px-8 py-6 text-center text-[11.5px] text-ink-500">
        Powered by <span className="font-semibold text-ink-700">Jewelitt Vault</span>
      </footer>
    </div>
  );
}

/** Warm tri-metal tints for creator tiles (matches the brand palette). */
const CREATOR_TINTS = [
  "linear-gradient(135deg,#C7A05C,#8F6D38)",
  "linear-gradient(135deg,#8F959D,#5C626A)",
  "linear-gradient(135deg,#C08A7D,#8A5648)",
  "linear-gradient(135deg,#8AA0A6,#556B70)",
  "linear-gradient(135deg,#B9A7C4,#7C6690)",
  "linear-gradient(135deg,#D6B474,#A0793D)",
];

function DesignCard({
  d,
  i,
  liked,
  reduce,
  onOpen,
  onLike,
  onShare,
}: {
  d: ExploreDesign;
  i: number;
  liked: boolean;
  reduce: boolean;
  onOpen: () => void;
  onLike: () => void;
  onShare: () => void;
}) {
  return (
    <motion.div variants={reduce ? undefined : { hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } }}>
      <div className="group relative overflow-hidden rounded-2xl border border-white/60 bg-white/50 shadow-sm backdrop-blur transition hover:shadow-pop">
        {/* poster */}
        <button onClick={onOpen} className="block aspect-[4/3] w-full cursor-pointer overflow-hidden">
          {d.posterUrl ? (
            <img src={d.posterUrl} alt={d.name} loading="lazy" className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.04]" />
          ) : (
            <div className="grid h-full w-full place-items-center bg-[radial-gradient(circle_at_50%_35%,#F6F1E8,#E4DBCB)] text-[var(--accent-strong,#a0793d)]/50">
              <Sparkles size={30} />
            </div>
          )}
        </button>

        {/* configurable badge */}
        {d.configurable && (
          <span className="pointer-events-none absolute left-2.5 top-2.5 rounded-full bg-black/45 px-2 py-0.5 text-[10px] font-semibold text-white backdrop-blur-sm">
            Customizable
          </span>
        )}

        {/* hover actions */}
        <div className="absolute right-2.5 top-2.5 flex gap-1.5 opacity-0 transition group-hover:opacity-100">
          <button
            onClick={onLike}
            title={liked ? "Unlike" : "Like"}
            className="grid h-8 w-8 place-items-center rounded-full bg-white/90 text-ink-700 shadow-sm backdrop-blur transition hover:scale-110"
          >
            <Heart size={15} className={liked ? "fill-[var(--red,#c2564e)] text-[var(--red,#c2564e)]" : ""} />
          </button>
          <button
            onClick={onShare}
            title="Share"
            className="grid h-8 w-8 place-items-center rounded-full bg-white/90 text-ink-700 shadow-sm backdrop-blur transition hover:scale-110"
          >
            <Share2 size={14} />
          </button>
        </div>
      </div>

      {/* meta row */}
      <div className="mt-2 flex items-center gap-2 px-0.5">
        {d.creator?.logo ? (
          <img src={d.creator.logo} alt="" className="h-6 w-6 shrink-0 rounded-full border border-black/10 object-cover" />
        ) : (
          <span
            className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-semibold text-white"
            style={{ background: CREATOR_TINTS[i % CREATOR_TINTS.length] }}
          >
            {d.creator?.name?.charAt(0) ?? "•"}
          </span>
        )}
        <span className="truncate text-[13.5px] font-semibold text-ink-900">{d.name}</span>
        <button onClick={onLike} className="ml-auto flex shrink-0 items-center gap-1 text-[12.5px] text-ink-500 hover:text-ink-800">
          <Heart size={13} className={liked ? "fill-[var(--red,#c2564e)] text-[var(--red,#c2564e)]" : ""} />
          {d.likeCount}
        </button>
      </div>
    </motion.div>
  );
}
